import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { firstAllowedRoute } from "../lib/modules";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { KeyRound } from "lucide-react";

export default function ChangePassword() {
  const { user, changePassword } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const forced = user?.needs_password_change;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("New password must be at least 8 characters.");
    if (pw !== confirm) return setErr("New password and confirmation do not match.");
    setBusy(true);
    try {
      await changePassword({ current_password: cur, new_password: pw });
      nav(forced ? firstAllowedRoute({ ...user, needs_password_change: false }) : "/", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8" style={{ background: "var(--cv-sand)" }}>
      <div
        className="w-full max-w-[440px] bg-white p-8"
        style={{ border: "1px solid var(--cv-line)", borderRadius: 14, boxShadow: "0 10px 30px rgba(60,45,30,0.10)" }}
        data-testid="change-password-card"
      >
        <div className="flex items-center gap-3 mb-1">
          <span className="ciso-chip"><KeyRound className="w-[18px] h-[18px]" /></span>
          <h1 className="text-[22px] font-bold small-caps" style={{ fontFamily: "Cambria, serif", color: "var(--cv-charcoal)" }}>
            {forced ? "Set a new password" : "Change password"}
          </h1>
        </div>
        <p className="text-[13px] mb-6" style={{ color: "var(--cv-dark-grey)" }}>
          {forced
            ? "Your account uses a temporary password. Please choose a new one to continue."
            : "Update the password for your account."}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cur">Current password</Label>
            <Input id="cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="cp-current" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw">New password</Label>
            <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="cp-new" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="cp-confirm" />
          </div>
          {err && <div className="text-[13px]" style={{ color: "var(--cv-crimson)" }} data-testid="cp-error">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full" data-testid="cp-submit"
            style={{ background: "linear-gradient(180deg, #C13818 0%, #A5300F 100%)", color: "#fff" }}>
            {busy ? "Saving…" : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
